import { ConnectionDisplayName } from '@app/modules/social-export/components/ConnectionDisplayName';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { SocialExportGetStationConnectionStateDto } from '@mi-front/api-v2';
import {
  Avatar,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
} from '@mui/material';
import { ReactNode } from 'react';

type ConnectionSelectProps = {
  connections: SocialExportGetStationConnectionStateDto[];
  value: string | null;
  onChange: (id: string | null) => void;
  platform: SocialPlatform;
  disabled?: boolean;
  size?: 'small' | 'medium';
  /** When set, renders an InputLabel on the Select. */
  label?: ReactNode;
  id?: string;
  sx?: SxProps<Theme>;
};

/**
 * Select to pick a social-export connection, showing profile photo + display name.
 */
export function ConnectionSelect({
  connections,
  value,
  onChange,
  platform,
  disabled = false,
  size = 'medium',
  label,
  id = 'connection-select',
  sx,
}: ConnectionSelectProps) {
  const selectedConnection = connections.find((c) => c.id === value) ?? null;
  const photo = selectedConnection?.profile.photo;
  const labelId = label ? `${id}-label` : undefined;
  const selectedAvatarSize = size === 'small' ? 22 : 32;
  const menuAvatarSize = size === 'small' ? 22 : 28;

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value || null);
  };

  return (
    <FormControl fullWidth size={size} sx={sx}>
      {label ? <InputLabel id={labelId}>{label}</InputLabel> : null}
      <Select
        id={id}
        labelId={labelId}
        label={label}
        value={value ?? ''}
        onChange={handleChange}
        disabled={disabled}
        displayEmpty={!label}
        startAdornment={
          selectedConnection ? (
            <InputAdornment position="start">
              <Avatar
                src={photo || undefined}
                sx={{ width: selectedAvatarSize, height: selectedAvatarSize }}
              >
                {!photo ? platform.Icon({ colored: true }) : null}
              </Avatar>
            </InputAdornment>
          ) : undefined
        }
        renderValue={(selectedId) => {
          const connection = connections.find((c) => c.id === selectedId);
          return connection ? (
            <ConnectionDisplayName connection={connection} component="span" />
          ) : (
            ''
          );
        }}
      >
        {connections.map((connection) => (
          <MenuItem key={connection.id} value={connection.id}>
            <ListItemIcon sx={{ minWidth: size === 'small' ? 36 : 40 }}>
              <Avatar
                src={connection.profile.photo || undefined}
                sx={{ width: menuAvatarSize, height: menuAvatarSize }}
              >
                {!connection.profile.photo ? platform.Icon({ colored: true }) : null}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              disableTypography
              primary={<ConnectionDisplayName connection={connection} component="span" />}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
